import { NestedFormsDataProps } from "components/ObjectTemplateCanvas/ObjectTemplateCanvas";
import { ObjectTemplateCardProps } from "components/ObjectTemplateCanvas/ObjectTemplateCard";
import { FeConfigType, FormField } from "config/config";
import {
    Direction,
    getPositionedGraph,
} from "containers/Resources/screens/ResourcesSingle/components/Visualization/visualization.service";
import { Object_Template } from "graphql/generated/graphql";
import { Dispatch, SetStateAction } from "react";
import { MarkerType } from "reactflow";

export const objectTemplateNodeType = "card";
export const objectTemplateReadOnlyNodeType = "readOnlyTemplateCard";
export const customObjectChildNodeType = "customObjectCard";
export const customObjectContainerNodeType = "group";
export const customNodeEdge = "customNodeEdge";

interface ObjectTemplateNode {
    height: number;
    width: number;
    position: { x: number; y: number };
    id: string;
    data: NodeDataProps;
    type: string;
    isConnectable: boolean;
}
interface CustomObjectNode {
    height: number;
    width: number;
    position: { x: number; y: number };
    id: string;
    data: any;
    type: string;
    isConnectable: boolean;
}

export interface NodeDataProps {
    value: Object_Template;
    layoutDirection: Direction;
    fetchObjectTemplateList: () => Promise<any>;
    setNestedFormsData: Dispatch<SetStateAction<{}>>;
    nestedFormsData: NestedFormsDataProps;
    deleteNewForm: () => void;
    formStructure?: FormField[];
    setLoading: (isLoading: boolean) => void;
    // Set by the deep-link focus decorator (canvas displayNodes memo) so the card
    // can render the focus/selection ring on its own element.
    isFocused?: boolean;
    // Interactive template canvas (item C1): the overview renders lightweight
    // read-only cards and mounts the heavy editable attributes-form only for the
    // ONE template the user expanded. expandedTemplateId names that template;
    // onExpandTemplate/onCollapseTemplate toggle it. In embed mode (readOnly) every
    // node stays read-only and these are left undefined.
    expandedTemplateId?: string | null;
    onExpandTemplate?: (id: string) => void;
    onCollapseTemplate?: () => void;
    // id → name of every template in the current graph, so a read-only overview
    // card can resolve an OBJECT_TEMPLATE_REFERENCE attribute to its target's name.
    referenceTargetNames?: { [id: string]: string };
}

const READ_ONLY_NODE_WIDTH = 250;
// ─── Read-only overview card (item C1) geometry. The card shows the full template
// read-only — icon + name + object-count + attribute list — instead of just the
// name (Margot: "display the full card, but read-only"). Its height is derived
// DETERMINISTICALLY from the loaded attribute count (same no-DOM-measure approach as
// the custom-object container) so dagre reserves the right space with no
// measure→relayout cycle. Keep these in sync with ObjectTemplateCard.module.scss
// (.readOnlyCard). Live-tune after deploy if a card clips or the gaps look off.
export const READ_ONLY_MAX_VISIBLE_ATTRS = 8;
const READ_ONLY_CARD_PADDING = 20; // formWrapper 10px top + 10px bottom
const READ_ONLY_HEADER_HEIGHT = 40; // icon + name (link) + edit pencil
const READ_ONLY_COUNT_HEIGHT = 22; // "N objects"
const READ_ONLY_ATTR_TITLE_HEIGHT = 24; // "Attributes" label
const READ_ONLY_ATTR_ROW_HEIGHT = 22; // one attribute row (name · type)
const READ_ONLY_ATTR_MORE_HEIGHT = 20; // "+N more" indicator row

const getReadOnlyCardHeight = (template: Object_Template): number => {
    const attributeList =
        template?.object_template_attribute_list_object_template_id
            ?.object_template_attribute_list ?? [];
    const attributeCount =
        template?.object_template_attribute_list_object_template_id
            ?.object_template_attribute_count ?? attributeList.length;
    const shownCount = Math.min(attributeList.length, READ_ONLY_MAX_VISIBLE_ATTRS);
    const hasMore = attributeCount > shownCount;
    const attributesBlock =
        shownCount > 0
            ? READ_ONLY_ATTR_TITLE_HEIGHT +
              shownCount * READ_ONLY_ATTR_ROW_HEIGHT +
              (hasMore ? READ_ONLY_ATTR_MORE_HEIGHT : 0)
            : 0;
    return (
        READ_ONLY_CARD_PADDING +
        READ_ONLY_HEADER_HEIGHT +
        READ_ONLY_COUNT_HEIGHT +
        attributesBlock
    );
};
// Option B canonical editable template-card height. This SAME value is pinned on
// the card CSS box in ObjectTemplateCard.module.scss (.react-flow__node-card
// .formWrapper { height }) AND declared here to dagre. Pinning every editable
// card to one height makes them uniform, so dagre's per-rank sizing is even
// (no top-bottom raggedness) and the left-right layout has no cross-axis overlap.
// The react-flow node wrapper adds a uniform ~16px padding on top of this height
// for EVERY node type (editable cards and custom-object containers alike), so the
// per-node gap stays consistent across both canvases. The card content (mainly the
// attribute list) flexes/scrolls to fill this fixed height rather than growing the
// card. Live-measured real content heights ranged ~296–466 against the old flat
// 400 declaration (the raggedness/overlap cause); 450 keeps a full attribute list
// visible without clipping the header/save/add controls, and shorter cards pad out
// to the same height uniformly.
const EDITABLE_NODE_HEIGHT = 450;
const EDITABLE_NODE_WIDTH = 250;
const EMBED_GRAPH_SPACING = 50;
const DEFAULT_GRAPH_SPACING = 50;

// ─── Canonical canvas node dimensions (Option B: one fixed size fed to BOTH the
// CSS box AND the dagre declaration, so the visible gap equals the spacing
// constant by construction and both canvases lay out identically). Keep these in
// sync with the widths pinned in CustomObjectNode.module.scss / ObjectTemplateCard.module.scss.
// The custom-object container declares its height DETERMINISTICALLY from its
// (loaded) child count — no DOM-measure→relayout cycle — using the per-child row
// height (real card height + inter-card gap). A container reserves ≈ HEADER +
// N·ROW + FOOTER (CONTAINER_HEADER_HEIGHT + N·CANVAS_CHILD_ROW_HEIGHT +
// CONTAINER_FOOTER_HEIGHT).
export const CANVAS_CONTAINER_WIDTH = 250;
// Height of a COLLAPSED child (custom-object) card. The card is pinned to this
// height in CustomObjectNode.module.scss (.collapsedChildCard) and the same value is
// fed to dagre, so the stack pitch is exactly card + gap.
export const CANVAS_CHILD_CARD_HEIGHT = 40;
// Horizontal inset of the child cards inside their container: the container's 8px
// padding plus its 1px border, so a card lines up with the container header rule.
export const CANVAS_CHILD_INSET = 9;
// Child card width: the container's inner width. Every card has the same width, so
// edges anchor on the card border. Pinned on the card in CustomObjectNode.module.scss
// (.collapsedChildCard).
export const CANVAS_CHILD_CARD_WIDTH = CANVAS_CONTAINER_WIDTH - 2 * CANVAS_CHILD_INSET;
// Gap between stacked child cards inside a container (the dagre nodesep passed to
// positionChildrenNodes). One design $spacing unit.
export const CANVAS_CHILD_GAP = 8;
// Per-child vertical footprint = real card height + inter-card gap. This is the dagre
// stack pitch (getChildNode height + positionChildrenNodes nodesep).
export const CANVAS_CHILD_ROW_HEIGHT = CANVAS_CHILD_CARD_HEIGHT + CANVAS_CHILD_GAP;
// react-flow y-offset of the FIRST child card within its container (the offsetTop
// passed to positionChildrenNodes). The cards are absolutely-positioned react-flow
// overlays that start this far below the container's top, clearing the header title.
export const CANVAS_CHILD_STACK_TOP_OFFSET = 45;
// Breathing room reserved between the last child card and the footer count line.
const CANVAS_FOOTER_GAP = 8;
// Height of the container header block in normal DOM flow: 8px padding + 1px border
// + the 28px header row + its 16px bottom margin. The child spacer starts here, so the
// footer count line lands just below the last card.
const CONTAINER_HEADER_HEIGHT = 53;
// Always-present interactive footer: 5px top margin + the 15px count line + the
// container's 16px bottom padding + 1px border. Keep in sync with
// CustomObjectNode.module.scss .containerFooter.
const CONTAINER_FOOTER_HEIGHT = 37;
const CONTAINER_EMBED_HEIGHT = 120;
// Extra footer height when a container shows the Load more split button
// (CustomObjectContainerNode, .loadMoreButtons): 3px margin + 2px gap + the 24px
// small button. Declaring it keeps the next rank of containers from overlapping.
const CONTAINER_LOAD_MORE_HEIGHT = 29;

// Deterministic height of the DOM spacer that reserves vertical room for a container's
// absolutely-positioned (react-flow) child cards, so the footer count line lands just
// below the last card in normal DOM flow. The cards render at
// CANVAS_CHILD_STACK_TOP_OFFSET below the container top with a per-card pitch of
// CANVAS_CHILD_ROW_HEIGHT (last card bottom = TOP_OFFSET + N·CARD + (N−1)·GAP), while
// this spacer begins BELOW the header (CONTAINER_HEADER_HEIGHT) — so it must cover the
// stack extent shifted by (top offset − header height) plus a small footer gap.
// Grounded end-to-end on develop (see item 3.1 measurement notes on the task).
export const getChildrenSpacerHeight = (loadedChildCount: number): number => {
    const n = Math.max(loadedChildCount, 0);
    if (n === 0) {
        return 0;
    }
    const stackExtent = n * CANVAS_CHILD_CARD_HEIGHT + (n - 1) * CANVAS_CHILD_GAP;
    return stackExtent + CANVAS_CHILD_STACK_TOP_OFFSET - CONTAINER_HEADER_HEIGHT + CANVAS_FOOTER_GAP;
};

// Deterministic container height from the number of loaded child objects, kept in sync
// with the visible DOM box (header + child spacer + footer) so dagre reserves at least
// the rendered height and the next rank of containers does not overlap (item 8). When
// the container renders the progressive-load block (hasMore), reserve its height too.
const getContainerHeight = (
    loadedChildCount: number,
    embedMode: boolean,
    hasMore = false
): number => {
    if (embedMode) {
        return CONTAINER_EMBED_HEIGHT;
    }
    return (
        CONTAINER_HEADER_HEIGHT +
        getChildrenSpacerHeight(loadedChildCount) +
        CONTAINER_FOOTER_HEIGHT +
        (hasMore ? CONTAINER_LOAD_MORE_HEIGHT : 0)
    );
};
// Horizontal gap between top-level canvas nodes (template cards / object-template
// containers). Deliberately tighter than the vertical (rank) gap: the nodes are tall
// and narrow, so the default square spacing left an unnecessarily wide horizontal gap
// between columns while the vertical spacing already looked right.
const CANVAS_HORIZONTAL_SPACING = 30;

const getNode = (data: NodeDataProps & { readOnly?: boolean }): ObjectTemplateNode | null => {
    if (!data.value?.id) {
        return null;
    }
    // Render the lightweight read-only card by default; mount the heavy editable
    // ObjectTemplateCard (a full attributes form ≈ 1 per template) only for the ONE
    // template the user expanded — so the overview does not pay that per-template
    // form cost across many templates (the several-seconds load + fan-whirr Margot
    // reported; operator-task-fe-canvas-loading-perf-followup, item C1). embedMode
    // (data.readOnly) forces every node read-only regardless.
    const isEditable =
        !data.readOnly &&
        !!data.expandedTemplateId &&
        data.value.id === data.expandedTemplateId;
    return {
        height: isEditable ? EDITABLE_NODE_HEIGHT : getReadOnlyCardHeight(data.value),
        width: isEditable ? EDITABLE_NODE_WIDTH : READ_ONLY_NODE_WIDTH,
        position: { x: 0, y: 0 },
        id: data.value.id,
        data,
        type: isEditable ? objectTemplateNodeType : objectTemplateReadOnlyNodeType,
        isConnectable: true,
    };
};

const getChildNode = (data) => {
    if (!data.value?.id || !data.value?.object_template_id) {
        return null;
    }
    return {
        height: CANVAS_CHILD_CARD_HEIGHT,
        width: CANVAS_CHILD_CARD_WIDTH,
        position: { x: 0, y: 0 },
        id: data.value.id,
        data,
        type: customObjectChildNodeType,
        isConnectable: true,
        parentId: data.value.object_template_id,
        extent: "parent" as "parent",
    };
};

const getParentNode = (data, embedMode = false, loadedChildCount = 0) => {
    if (!data.value?.id) {
        return null;
    }
    return {
        height: getContainerHeight(loadedChildCount, embedMode),
        width: embedMode ? 260 : CANVAS_CONTAINER_WIDTH,
        position: { x: 0, y: 0 },
        id: data.value.id,
        data,
        isConnectable: false,
        selectable: false,
        type: customObjectContainerNodeType,
    };
};

export const getPositionedGraphList = ({
    objectTemplateList,
    location,
    locationId,
    preFiltered = false,
    additionalData,
    layoutDirection,
}: {
    objectTemplateList: Object_Template[];
    location?: FeConfigType;
    locationId?: string;
    preFiltered?: boolean;
    additionalData: ObjectTemplateCardProps;
    layoutDirection: Direction;
}) => {
    const edgesList: { id: string; source: string; target: string }[] = [];
    const nodesList: ObjectTemplateNode[] = [];

    let objectTemplateListData: { [key: string]: Object_Template } = {};

    objectTemplateList
        .filter((objectTemplate) => objectTemplate?.id)
        .forEach((objectTemplate) => {
            objectTemplateListData[objectTemplate.id] = objectTemplate;
        });

    let filteredObjectTemplateData: { [key: string]: Object_Template } = {};

    for (const objectTemplateKey in objectTemplateListData) {
        const objectTemplateValue = objectTemplateListData[objectTemplateKey];
        if (
            preFiltered ||
            location === "workspace" ||
            objectTemplateValue.project_id === locationId ||
            objectTemplateValue.bundle_id === locationId
        ) {
            filteredObjectTemplateData[objectTemplateKey] = objectTemplateValue;

            objectTemplateValue.object_template_attribute_plain_list_reference_object_template_id?.forEach(
                (reference) => {
                    if (!reference?.object_template_id || !reference?.id) {
                        return;
                    }
                    const referencedTemplate =
                        objectTemplateListData[reference.object_template_id];
                    if (!referencedTemplate?.id) {
                        return;
                    }
                    filteredObjectTemplateData[reference.object_template_id] = referencedTemplate;
                    const edge = {
                        id: reference.id,
                        target: objectTemplateValue.id,
                        source: reference.object_template_id,
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 20,
                            height: 20,
                        },
                    };
                    edgesList.push(edge);
                }
            );
        }
    }

    // id → name of every template in scope, so a read-only overview card can label
    // its OBJECT_TEMPLATE_REFERENCE attributes with the target template's name.
    const referenceTargetNames: { [id: string]: string } = {};
    Object.values(objectTemplateListData).forEach((template) => {
        if (template?.id && template?.name) {
            referenceTargetNames[template.id] = template.name;
        }
    });

    Object.values(filteredObjectTemplateData).forEach((value) => {
        const node = getNode({
            value,
            layoutDirection,
            referenceTargetNames,
            ...additionalData,
        });
        if (node) {
            nodesList.push(node);
        }
    });

    const graphSpacing = additionalData.readOnly ? EMBED_GRAPH_SPACING : DEFAULT_GRAPH_SPACING;
    const [positionedNodes, edges] = getPositionedGraph(
        nodesList,
        edgesList,
        0,
        0,
        layoutDirection,
        graphSpacing,
        true,
        CANVAS_HORIZONTAL_SPACING,
        // Top-align (vertical) / left-align (horizontal) the template containers within
        // each rank, instead of dagre's default cross-axis centering (item 5).
        true
    );

    return [positionedNodes, edges];
};

export const positionChildrenNodes = (positionedNodes, childrenNodeData) => {
    Object.entries(childrenNodeData).forEach(([objectTemplateId, childrenNodeList]) => {
        const [positionedChildrenNodes] = getPositionedGraph(
            childrenNodeList as any,
            [],
            CANVAS_CHILD_INSET,
            CANVAS_CHILD_STACK_TOP_OFFSET,
            Direction.Horizontal,
            // nodesep = the vertical gap between stacked child cards. The children are
            // disconnected (no edges) so dagre places them in one rank separated by
            // nodesep; combined with the real card height fed as node.height this
            // yields a pitch of CARD_HEIGHT + GAP (no overlap — was 40 + 20 = 60 < 82).
            CANVAS_CHILD_GAP
        );
        positionedNodes.push(...positionedChildrenNodes);
    });
};

interface CustomObjectCardProps {
    fetchCustomObjectList: () => Promise<any>;
    formStructure?: FormField[];
    setLoading: (isLoading: boolean) => void;
    expandedId?: string | null;
    setExpandedId?: (id: string) => void;
    /** When true, skip viewport culling (dashboard embed). */
    embedMode?: boolean;
    /** When true, hide create/edit affordances. */
    readOnly?: boolean;
    /**
     * Load the next page of custom objects (or all of them, when loadAll) for a
     * template container that was only partially loaded on initial paint.
     * Wired only on the interactive object canvas (not the read-only embed).
     */
    loadMoreForTemplate?: (templateId: string, loadAll?: boolean) => void;
}

export const getPositionedCustomObjectGraphList = ({
    objectTemplateList,
    location,
    locationId,
    preFiltered = false,
    additionalData,
    layoutDirection,
    pinnedContainerPositions,
}: {
    objectTemplateList: Object_Template[];
    location?: FeConfigType;
    locationId?: string;
    preFiltered?: boolean;
    additionalData: CustomObjectCardProps;
    layoutDirection: Direction;
    /**
     * Positions (by container/object_template id) of containers already placed on
     * the canvas. When supplied — e.g. on a per-container "load more" — those
     * containers keep their existing position instead of being re-laid-out by
     * dagre, so revealing more objects in one container does not reshuffle the
     * whole graph. Child nodes carry parent-relative coordinates and follow their
     * container automatically; only newly-appearing containers get fresh dagre
     * positions.
     */
    pinnedContainerPositions?: { [id: string]: { x: number; y: number } };
}) => {
    // Child-level edges (custom object IDs) — used by ReactFlow to render connections
    const edgesList: {
        id: string;
        source: string;
        target: string;
        type?: string;
        markerStart?: { type: MarkerType; width: number; height: number };
    }[] = [];
    // Template-level edges — used only by dagre so connected containers rank correctly
    const parentEdgesList: { id: string; source: string; target: string }[] = [];
    const seenParentEdges = new Set<string>();
    const nodesList: CustomObjectNode[] = [];

    const objectTemplateData = Object.fromEntries(
        objectTemplateList
            .filter((objectTemplate) => objectTemplate?.id)
            .map((objectTemplate) => [objectTemplate.id, objectTemplate])
    );
    const customObjects: any[] = [];
    objectTemplateList.forEach((objectTemplate) => {
        if (!objectTemplate?.id) {
            return;
        }
        objectTemplate.custom_object_list_object_template_id?.custom_object_list?.forEach(
            (customObject) => {
                if (customObject?.id) {
                    customObjects.push(customObject);
                }
            }
        );
    });
    const customObjectData = Object.fromEntries(
        customObjects
            .filter((customObject) => customObject?.id)
            .map((customObject) => [customObject.id, customObject])
    );

    let objectTemplateNodeData: any = {};

    const hasCurrentLocation = (item) =>
        preFiltered ||
        location === "workspace" ||
        item.project_id === locationId ||
        item.bundle_id === locationId;

    const setChildNode = (objectTemplateId: string, customObjectId: string) => {
        const customObject = customObjectData[customObjectId];
        const objectTemplate = objectTemplateData[objectTemplateId];
        if (!customObject?.id || !objectTemplate?.id) {
            return;
        }
        const hasCustomObjectId = objectTemplateNodeData[objectTemplateId]?.find(
            (item) => item.id === customObjectId
        );
        if (hasCustomObjectId) {
            return;
        }
        if (!objectTemplateNodeData[objectTemplateId]) {
            const parentNode = getParentNode(
                {
                    value: objectTemplate,
                    layoutDirection,
                    children: [],
                    ...additionalData,
                },
                additionalData.embedMode
            );
            if (!parentNode) {
                return;
            }
            nodesList.push(parentNode);
            objectTemplateNodeData[objectTemplateId] = [];
        }
        const childNode = getChildNode({
            value: customObject,
            layoutDirection,
            ...additionalData,
        });
        if (!childNode) {
            return;
        }
        objectTemplateNodeData[objectTemplateId] = [
            ...(objectTemplateNodeData[objectTemplateId] ?? []),
            childNode,
        ];
    };

    objectTemplateList.forEach((objectTemplate) => {
        if (!objectTemplate?.id) {
            return;
        }
        const objectTemplateNodeId = objectTemplate.id;
        // get all object_templates in a current location and all their custom_objects
        if (hasCurrentLocation(objectTemplate)) {
            if (!objectTemplateNodeData[objectTemplateNodeId]) {
                const parentNode = getParentNode(
                    {
                        value: objectTemplate,
                        layoutDirection,
                        children: [],
                        ...additionalData,
                    },
                    additionalData.embedMode
                );
                if (parentNode) {
                    nodesList.push(parentNode);
                    objectTemplateNodeData[objectTemplateNodeId] = [];
                }
            }

            objectTemplate.custom_object_list_object_template_id?.custom_object_list?.forEach(
                (customObject) => {
                    if (customObject?.id) {
                        setChildNode(objectTemplateNodeId, customObject.id);
                    }
                }
            );
        } else {
            // get all custom_objects in a current location
            objectTemplate.custom_object_list_object_template_id?.custom_object_list?.forEach(
                (customObject) => {
                    if (hasCurrentLocation(customObject) && customObject?.id) {
                        setChildNode(objectTemplateNodeId, customObject.id);
                    }
                }
            );
        }
    });

    // Reference edges — BOUNDED to the initially-loaded batch. Previously
    // setChildNode recursively pulled every referencing custom object into the
    // graph as a new child, so each container grew to its incoming-reference
    // fan-in instead of the loaded batch (K per template). That inflated the
    // whole canvas bounding box (fitView then framed empty spacer) and defeated
    // the "load only a sensible amount at first" design. We now draw edges only
    // between custom objects that are actually loaded/rendered; hidden neighbours
    // are surfaced via the per-container "Showing n of m" counts / Load more.
    const loadedCustomObjectIds = new Set<string>();
    Object.values(objectTemplateNodeData).forEach((children: any) => {
        (children ?? []).forEach((child: any) => {
            if (child?.id) {
                loadedCustomObjectIds.add(child.id);
            }
        });
    });
    const seenChildEdges = new Set<string>();
    customObjects.forEach((customObject) => {
        if (!customObject?.id || !loadedCustomObjectIds.has(customObject.id)) {
            return;
        }
        const sourceTemplateId = customObject.object_template_id;
        customObject?.referencing_custom_objects?.forEach((reference: any) => {
            const referencedObjectTemplateId = reference?.object_template_id;
            if (
                !reference?.id ||
                !referencedObjectTemplateId ||
                !loadedCustomObjectIds.has(reference.id)
            ) {
                return;
            }
            const childEdgeKey = `${customObject.id}-${reference.id}`;
            if (!seenChildEdges.has(childEdgeKey)) {
                seenChildEdges.add(childEdgeKey);
                edgesList.push({
                    id: childEdgeKey,
                    source: customObject.id,
                    target: reference.id,
                    type: customNodeEdge,
                    markerStart: {
                        type: MarkerType.ArrowClosed,
                        width: 10,
                        height: 10,
                    },
                });
            }
            // Template-level edge so dagre can rank connected containers
            if (sourceTemplateId && sourceTemplateId !== referencedObjectTemplateId) {
                const parentEdgeKey = `${sourceTemplateId}→${referencedObjectTemplateId}`;
                if (!seenParentEdges.has(parentEdgeKey)) {
                    seenParentEdges.add(parentEdgeKey);
                    parentEdgesList.push({
                        id: `parent-${parentEdgeKey}`,
                        source: sourceTemplateId,
                        target: referencedObjectTemplateId,
                    });
                }
            }
        });
    });

    const nodeListWithChildren = nodesList.map((parentNode) => {
        const childCount = objectTemplateNodeData[parentNode.id]?.length ?? 0;
        // Mirror CustomObjectContainerNode's `hasMore`: the container renders the
        // "Showing n of m" + Load more/Load all block when more objects exist than
        // are loaded (interactive canvas only). Reserve its height so dagre does not
        // let the next rank overlap it.
        const template = (parentNode.data as { value?: Object_Template })?.value;
        const containerLoaded =
            template?.custom_object_list_object_template_id?.custom_object_list?.length ?? 0;
        const containerTotal =
            template?.custom_object_list_object_template_id?.custom_object_count ?? 0;
        const hasMore =
            !additionalData.readOnly &&
            !!additionalData.loadMoreForTemplate &&
            containerLoaded < containerTotal;
        return {
            ...parentNode,
            // Declare the true (deterministic) container height from the loaded
            // child count so dagre reserves the space the container actually
            // occupies — otherwise the flat 500px declaration ballooned the
            // vertical gaps on containers with few objects.
            height: additionalData.embedMode
                ? parentNode.height
                : getContainerHeight(childCount, false, hasMore),
            data: { ...parentNode.data, children: objectTemplateNodeData[parentNode.id] },
        };
    });

    // Match the object-template canvas rank spacing so both canvases lay out
    // identically (was 60 here vs 50 there — an accidental divergence).
    const graphSpacing = additionalData.embedMode ? EMBED_GRAPH_SPACING : DEFAULT_GRAPH_SPACING;
    const [positionedNodes] = getPositionedGraph(
        nodeListWithChildren,
        parentEdgesList,
        0,
        0,
        layoutDirection,
        graphSpacing,
        true,
        CANVAS_HORIZONTAL_SPACING,
        // Top-align (vertical) / left-align (horizontal) the template containers within
        // each rank, instead of dagre's default cross-axis centering (item 5). Must match
        // getPositionedGraphList (object-templates view) so both canvases align identically;
        // this custom-objects view is the one Margot's item-5 report is about.
        true
    );
    positionChildrenNodes(positionedNodes, objectTemplateNodeData);

    // Incremental/pinned layout: keep already-placed containers where they are so
    // an interactive "load more" (or expand) only grows one container instead of
    // re-shuffling the whole graph. Child nodes use parent-relative coordinates,
    // so they move with their container without any adjustment here.
    if (pinnedContainerPositions) {
        positionedNodes.forEach((node) => {
            if (!node.parentId && pinnedContainerPositions[node.id]) {
                node.position = { ...pinnedContainerPositions[node.id] };
            }
        });
    }

    return [positionedNodes, edgesList];
};

/**
 * Given the current (post-drag) ordering of the loaded object-template attributes,
 * return the minimal set of {id, sort_order} patches needed so each attribute's
 * persisted sort_order equals its new index. Attributes whose position is unchanged
 * (or which carry no id) are skipped. Pure helper for the drag-reorder control.
 */
export const getAttributeSortOrderUpdates = (
    cards: ReadonlyArray<
        { id?: string | null; sort_order?: number | null } | null | undefined
    >
): { id: string; sort_order: number }[] => {
    const updates: { id: string; sort_order: number }[] = [];
    (cards ?? []).forEach((card, index) => {
        if (card?.id && card.sort_order !== index) {
            updates.push({ id: card.id, sort_order: index });
        }
    });
    return updates;
};
